import { useEffect, useState } from 'react'
import { Clear, Search } from '@mui/icons-material'
import { IconButton, TextField, useTheme } from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

const SearchTextField = ({
    debounceDelay = 800,
    debounceInput = true,
    disabled = false,
    label,
    onChange,
    placeholder,
    sx = [],
    value,
}) => {
    const theme = useTheme()
    const [searchTerm, setSearchTerm] = useState(value)

    useEffect(() => {
        setSearchTerm(value)
    }, [value])

    const searchTermChangedHandler = useDebouncedCallback(
        (newValue) => {
            if (typeof onChange === 'function') {
                onChange(newValue)
            }
        },
        !debounceInput ? 0 : debounceDelay
    )

    const handleSearchTermChange = (event) => {
        setSearchTerm(event.target.value)
        searchTermChangedHandler(event.target.value)
    }

    const handleClearSearch = () => {
        setSearchTerm('')
        // Clearing should be immediate, so skip the debounce and cancel any pending call.
        searchTermChangedHandler.cancel()
        if (typeof onChange === 'function') {
            onChange('')
        }
    }

    return (
        <TextField
            disabled={disabled}
            label={label}
            placeholder={placeholder}
            size="small"
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search
                                sx={{
                                    color: 'text.primary',
                                }}
                            />
                        </InputAdornment>
                    ),
                    endAdornment: searchTerm ? (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="Clear search"
                                disabled={disabled}
                                edge="end"
                                size="small"
                                onClick={handleClearSearch}
                            >
                                <Clear fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ) : null,
                },
            }}
            sx={[{ backgroundColor: theme.palette.background.paper }, ...(Array.isArray(sx) ? sx : [sx])]}
            value={searchTerm}
            variant="outlined"
            onChange={handleSearchTermChange}
        />
    )
}

SearchTextField.propTypes = {
    debounceDelay: PropTypes.number,
    debounceInput: PropTypes.bool,
    disabled: PropTypes.bool,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    sx: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])),
        PropTypes.func,
        PropTypes.object,
    ]),
    value: PropTypes.string,
    onChange: PropTypes.func,
}

export default SearchTextField
